import { hcWithType } from "@oblique/api/client"
import type { ClientResponse } from "hono/client"

const runtimeEnv = () =>
	(
		globalThis as typeof globalThis & {
			process?: { env?: Record<string, string> }
		}
	).process?.env ?? {}

const isLocalBrowserTarget = () => {
	if (typeof window === "undefined") {
		return false
	}

	return (
		window.location.protocol === "file:" ||
		window.location.hostname === "localhost" ||
		window.location.hostname === "127.0.0.1" ||
		window.location.hostname === "::1"
	)
}

export const resolveApiBaseUrl = (): string => {
	const env = runtimeEnv()

	if (env.BUN_PUBLIC_API_ORIGIN) {
		return env.BUN_PUBLIC_API_ORIGIN
	}

	if (typeof window !== "undefined") {
		return isLocalBrowserTarget()
			? "http://localhost:3001"
			: "https://api.oblique.tools"
	}

	return env.NODE_ENV === "production"
		? "https://api.oblique.tools"
		: "http://localhost:3001"
}

export const createApiClient = (baseUrl: string = resolveApiBaseUrl()) =>
	hcWithType(baseUrl, {
		fetch: (input: RequestInfo | URL, init?: RequestInit) =>
			fetch(input, {
				credentials: "include",
				...init,
			}),
	})

export const apiClient = createApiClient()

class HonoError extends Error {
	readonly status?: number
	constructor(message: string, status?: number, cause?: unknown) {
		super(message, cause ? { cause } : undefined)
		this.name = "HonoError"
		this.status = status
	}
}

type ExtractResponseData<T> =
	T extends ClientResponse<infer U, infer _E, infer _P> ? U : never

type ErrorPayload = {
	error?: {
		message?: string
		[key: string]: unknown
	}
}

const isErrorPayload = (value: unknown): value is ErrorPayload =>
	typeof value === "object" && value !== null && "error" in value

export const getData = async <
	T extends ClientResponse<unknown, number, string>,
>(
	response: T,
): Promise<ExtractResponseData<T>> => {
	try {
		const json = (await response.json()) as unknown

		if (isErrorPayload(json) && json.error) {
			const message =
				typeof json.error.message === "string"
					? json.error.message
					: "Unknown error"

			throw new HonoError(message, response.status, json.error)
		}

		return json as ExtractResponseData<T>
	} catch (error) {
		if (error instanceof HonoError) {
			throw error
		}

		throw new HonoError("Failed to parse response", response.status, error)
	}
}
